import React, { useState } from 'react';

import { IInsightPreview } from 'components/widget/common/styled';
import {
  CategorySectionStyled,
  Wrapper,
  NewConstructionWrapper,
  CategorySectionWrapper,
  SectionsWrapper,
} from '../styled';
import { CategoryTitle } from '../CategoryTitle';
import { ConstructionItem } from '../ConstructionItem';
import { InsightSection } from 'store/state/insightsContext';
import { InsightType, MarketplaceType } from 'utils/entities';
import { ShowMoreConstructions } from '../ShowMoreConstructions';
import { useLocale } from 'locale';
import CarouselStories from 'components/view-pager-to-stories';
import { useTheme } from 'hooks/useTheme';
import { NoConstructionsBlock } from '../NoConstructionsBlock';
import { InsightsSectionItemInnerPadding } from 'ds/components/Insights';
import WidgetWrapper from 'components/widget/common/widget-wrapper';
import { WithContentSlotSidePaddingContainer } from 'ds/components/Layout';
import { SeeOnMapButton } from 'components/see-on-map-button';
import { ShowAt } from 'consts/breakpoints';
import { INewDevelopmentJob, ConstructionTypes } from 'store/state/selectors/insights/summaryTypes';
import { useRoute } from 'config/routes';
import WidgetCollapsedWrapper from '../../common/widget-collapsed-wrapper';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { marketplaceSelector } from 'store/state/selectors/router';
import { isNewItemPageRoute } from 'utils/marketplaceRoutes';

export interface NewConstructionProps {
  onlyAccordion?: boolean;
  loading: boolean;
  inFiling: INewDevelopmentJob[];
  inFilingApproved: INewDevelopmentJob[];
  inConstruction: INewDevelopmentJob[];
  carouselData: IInsightPreview[];
  setActiveInsightId: (id: string) => void;
  expandNewUnitPageMap: (v: boolean) => void;
  setActiveConstructionId: (id: string | number) => void;
  onSeeOnMapClick?: () => void;
  noTitle?: boolean;
  marketplace: MarketplaceType;
  isCollapsed: boolean;
  showCarousel: boolean;
}

const ConstructionWidget: React.FunctionComponent<NewConstructionProps> = React.memo((props) => {
  const {
    onlyAccordion,
    loading,
    inFiling,
    inFilingApproved,
    inConstruction,
    carouselData,
    setActiveInsightId,
    setActiveConstructionId,
    onSeeOnMapClick,
    noTitle,
    marketplace,
    isCollapsed,
    showCarousel,
    expandNewUnitPageMap,
  } = props;
  const [ slideIndex, setSlideIndex ] = useState(0);
  const { colors } = useTheme();
  const { t } = useLocale();
  const { route } = useRoute();
  const visibleRows = 3;
  const isExpandActive = route.params.insightId && route.params.insightCollection;
  const inFilingJoined = [ ...inFiling, ...inFilingApproved ];
  const widgetHasInConstruction = inConstruction && inConstruction.length;
  const widgetHasInFiling = inFilingJoined && inFilingJoined.length;
  const widgetHasCarouselData = carouselData && carouselData.length;
  const isCommercial = marketplace === MarketplaceType.Commercial;

  const handleMapOpen = () => {
    if (isNewItemPageRoute(route.name)) {
      expandNewUnitPageMap(true);
    }
    onSeeOnMapClick();
  };

  const constructionCategories: INewDevelopmentJob[][] = [ inConstruction, inFilingJoined ];

  const maybeRenderList = () => {
    return (
      <Wrapper>
        {widgetHasInConstruction || widgetHasInFiling ? (
          <SectionsWrapper data-auto="construction-details">
            {constructionCategories.map((category: INewDevelopmentJob[], id: number) => (
              <CategorySectionWrapper key={id}>
                <CategoryTitle
                  type={id === 0 ? ConstructionTypes.IN_CONSTRUCTION : ConstructionTypes.IN_FILING}
                  amount={category.length}
                />

                {category.length ? (
                  <CategorySectionStyled>
                    {category
                      .slice(0, visibleRows)
                      .map((itemProps: INewDevelopmentJob, index: number) => (
                        <NewConstructionWrapper
                          key={index}
                          onMouseEnter={() => setActiveConstructionId(itemProps.geoId)}
                          onMouseLeave={() => setActiveConstructionId(null)}
                        >
                          <ConstructionItem {...itemProps} />
                        </NewConstructionWrapper>
                      ))
                    }

                    <ShowMoreConstructions
                      setActiveConstructionId={setActiveConstructionId}
                      visibleRows={visibleRows}
                      items={category}
                    />
                  </CategorySectionStyled>
                ) : null}
              </CategorySectionWrapper>
            ))}
            <ShowAt to={2}>
              {!isExpandActive ? <SeeOnMapButton onClick={handleMapOpen} type={InsightType.Planning} /> : null}
            </ShowAt>
          </SectionsWrapper>
        ) : (<NoConstructionsBlock isCommercial={isCommercial} />)}
      </Wrapper>
    );
  };

  const renderAccordion = () => {
    return (
      <WithContentSlotSidePaddingContainer noMobilePadding>
        {widgetHasCarouselData ? (
          <InsightsSectionItemInnerPadding>
            {maybeRenderList()}
          </InsightsSectionItemInnerPadding>
        ) : maybeRenderList()}
      </WithContentSlotSidePaddingContainer>
    );
  };

  const carousel = (
    <CarouselStories
      insights={carouselData}
      onReadMoreClick={setActiveInsightId}
      slideIndex={slideIndex}
      onSlideIndexChange={(idx) => setSlideIndex(idx)}
      color={colors.insights.planning}
    />
  );

  const wrapperProps = {
    carousel: widgetHasCarouselData && showCarousel ? carousel : null,
    targetId: InsightSection.Planning,
    styleType: InsightType.Planning,
    title: noTitle ? null : t('widgetTitle.newConstructions'),
  };

  return (
    <>
      {
        onlyAccordion ? renderAccordion() : (
          loading ? null : (
            isCollapsed
              ? (
                <WidgetCollapsedWrapper
                  {...wrapperProps}
                  dynamicContentData={(widgetHasInConstruction || widgetHasInFiling) ? constructionCategories : null}
                >
                  {maybeRenderList()}
                </WidgetCollapsedWrapper>
              ) : <WidgetWrapper {...wrapperProps}>{maybeRenderList()}</WidgetWrapper>
          )
        )
      }
    </>
  );
});

const mapStateToProps = (state: State) => ({
  marketplace: marketplaceSelector(state),
});

export default connect(mapStateToProps)(ConstructionWidget);
